import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import type {
  AdminLessonsResponseDto,
  AdminStudentsResponseDto,
  CreateEnrollmentRequestDto,
  EnrollmentDto,
  EnrollmentStatus,
  EnrollmentType,
  EnrollmentsResponseDto,
  UpdateEnrollmentRequestDto
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAdminLessons } from "../../shared/api/adminLesson";
import {
  convertEnrollmentToRegular,
  createEnrollment,
  fetchAdminEnrollments,
  fetchAdminStudents,
  patchEnrollment,
  transitionEnrollment
} from "../../shared/api/adminRoster";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/students", label: "生徒" },
  { to: "/t/:tenantSlug/teacher/admin/lessons", label: "レッスン" }
];

type EnrollmentCreateFormState = {
  studentId: string;
  lessonId: string;
  enrollmentType: EnrollmentType;
  status: "" | EnrollmentStatus;
  joinDate: string;
  leaveDate: string;
  notesInternal: string;
};

type EnrollmentEditFormState = {
  status: EnrollmentStatus;
  joinDate: string;
  leaveDate: string;
  notesInternal: string;
};

function createInitialEnrollmentCreateFormState(): EnrollmentCreateFormState {
  return {
    studentId: "",
    lessonId: "",
    enrollmentType: "trial",
    status: "",
    joinDate: "",
    leaveDate: "",
    notesInternal: ""
  };
}

function createEditStateMap(enrollments: EnrollmentsResponseDto | null): Record<string, EnrollmentEditFormState> {
  if (!enrollments) {
    return {};
  }

  return Object.fromEntries(
    enrollments.items.map((enrollment) => [
      enrollment.enrollmentId,
      {
        status: enrollment.status,
        joinDate: enrollment.joinDate,
        leaveDate: enrollment.leaveDate ?? "",
        notesInternal: enrollment.notesInternal ?? ""
      }
    ])
  );
}

function buildCreateEnrollmentRequest(form: EnrollmentCreateFormState): CreateEnrollmentRequestDto {
  return {
    studentId: form.studentId,
    lessonId: form.lessonId,
    enrollmentType: form.enrollmentType,
    status: form.status || null,
    joinDate: form.joinDate,
    leaveDate: form.leaveDate || null,
    notesInternal: form.notesInternal.trim() || null
  };
}

function buildUpdateEnrollmentRequest(form: EnrollmentEditFormState): UpdateEnrollmentRequestDto {
  return {
    status: form.status,
    joinDate: form.joinDate,
    leaveDate: form.leaveDate || null,
    notesInternal: form.notesInternal.trim() || null
  };
}

function getTransitionActions(status: EnrollmentStatus): EnrollmentStatus[] {
  switch (status) {
    case "pendingStart":
      return ["active", "cancelled"];
    case "active":
      return ["paused", "ended"];
    case "paused":
      return ["active", "ended"];
    default:
      return [];
  }
}

export function AdminEnrollmentsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [lessons, setLessons] = useState<AdminLessonsResponseDto | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentsResponseDto | null>(null);
  const [createForm, setCreateForm] = useState<EnrollmentCreateFormState>(createInitialEnrollmentCreateFormState());
  const [editForms, setEditForms] = useState<Record<string, EnrollmentEditFormState>>({});
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextStudents, nextLessons, nextEnrollments] = await Promise.all([
        fetchAdminStudents(tenantSlug),
        fetchAdminLessons(tenantSlug),
        fetchAdminEnrollments(tenantSlug)
      ]);

      setStudents(nextStudents);
      setLessons(nextLessons);
      setEnrollments(nextEnrollments);
      setEditForms(createEditStateMap(nextEnrollments));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.enrollment}情報・${tenantLabels.student}・${tenantLabels.lesson}の取得に失敗しました`));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshPage();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  async function handleCreateEnrollment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-enrollment");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const created = await createEnrollment(tenantSlug, buildCreateEnrollmentRequest(createForm));
      setCreateForm(createInitialEnrollmentCreateFormState());
      setFeedbackMessage(`${tenantLabels.enrollment}を作成しました: ${created.studentDisplayName} / ${created.lessonTitle}`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.enrollment}の作成に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  async function handlePatchEnrollment(enrollmentId: string) {
    const editForm = editForms[enrollmentId];

    if (!editForm) {
      return;
    }

    setBusyKey(`patch:${enrollmentId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await patchEnrollment(tenantSlug, enrollmentId, buildUpdateEnrollmentRequest(editForm));
      setFeedbackMessage(`${tenantLabels.enrollment}を更新しました。`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.enrollment}の更新に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleTransition(enrollment: EnrollmentDto, toStatus: EnrollmentStatus) {
    setBusyKey(`transition:${enrollment.enrollmentId}:${toStatus}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await transitionEnrollment(tenantSlug, enrollment.enrollmentId, { toStatus });
      setFeedbackMessage(`status を ${toStatus} に変更しました。`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "enrollment status 変更に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleConvertToRegular(enrollment: EnrollmentDto) {
    setBusyKey(`convert:${enrollment.enrollmentId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const nextJoinDate = editForms[enrollment.enrollmentId]?.joinDate || enrollment.joinDate;
      await convertEnrollmentToRegular(tenantSlug, enrollment.enrollmentId, {
        joinDate: nextJoinDate,
        notesInternal: editForms[enrollment.enrollmentId]?.notesInternal || enrollment.notesInternal
      });
      setFeedbackMessage("trial enrollment を regular に変換しました。");
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "trial から regular への変換に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="所属"
      description="生徒とレッスンを結び、体験・通常の所属状態を管理する画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>所属情報・生徒・レッスンを読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>所属作成</h2>
              <form
                onSubmit={(event) => void handleCreateEnrollment(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>生徒</span>
                  <select
                    value={createForm.studentId}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, studentId: event.target.value }))
                    }
                  >
                    <option value="">選択してください</option>
                    {students?.items.map((student) => (
                      <option key={student.studentId} value={student.studentId}>
                        {student.displayName} / {student.memberCode}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>レッスン</span>
                  <select
                    value={createForm.lessonId}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, lessonId: event.target.value }))
                    }
                  >
                    <option value="">選択してください</option>
                    {lessons?.items.map((lesson) => (
                      <option key={lesson.lessonId} value={lesson.lessonId}>
                        {lesson.title} / {describeStatus(lesson.lessonKind)}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>所属種別</span>
                  <select
                    value={createForm.enrollmentType}
                    onChange={(event) =>
                      setCreateForm((current) => ({
                        ...current,
                        enrollmentType: event.target.value === "trial" ? "trial" : "regular"
                      }))
                    }
                  >
                    <option value="trial">{describeStatus("trial")}</option>
                    <option value="regular">{describeStatus("regular")}</option>
                  </select>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>ステータス</span>
                  <select
                    value={createForm.status}
                    onChange={(event) =>
                      setCreateForm((current) => ({
                        ...current,
                        status: event.target.value as EnrollmentCreateFormState["status"]
                      }))
                    }
                  >
                    <option value="">{describeStatus("auto")}</option>
                    <option value="pendingStart">{describeStatus("pendingStart")}</option>
                    <option value="active">{describeStatus("active")}</option>
                    <option value="paused">{describeStatus("paused")}</option>
                    <option value="ended">{describeStatus("ended")}</option>
                    <option value="cancelled">{describeStatus("cancelled")}</option>
                  </select>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>開始日</span>
                  <input
                    type="date"
                    value={createForm.joinDate}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, joinDate: event.target.value }))
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>終了日</span>
                  <input
                    type="date"
                    value={createForm.leaveDate}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, leaveDate: event.target.value }))
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>内部メモ</span>
                  <textarea
                    value={createForm.notesInternal}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, notesInternal: event.target.value }))
                    }
                    rows={3}
                  />
                </label>

                <button type="submit" disabled={busyKey === "create-enrollment"}>
                  {busyKey === "create-enrollment" ? "作成中..." : "所属を作成"}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>所属一覧</h2>
              {enrollments?.items.length === 0 ? <p style={{ margin: 0 }}>所属はまだありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {enrollments?.items.map((enrollment) => {
                  const editForm = editForms[enrollment.enrollmentId];

                  if (!editForm) {
                    return null;
                  }

                  return (
                    <article
                      key={enrollment.enrollmentId}
                      style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
                    >
                      <div style={{ display: "grid", gap: 4 }}>
                        <strong>
                          {enrollment.studentDisplayName} / {enrollment.lessonTitle}
                        </strong>
                        <span style={{ fontSize: 14, opacity: 0.8 }}>
                          {describeStatus(enrollment.enrollmentType)} / {describeStatus(enrollment.status)}
                        </span>
                        <span style={{ fontSize: 12, opacity: 0.7 }}>{enrollment.enrollmentId}</span>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        <label style={{ display: "grid", gap: 4 }}>
                          <span>ステータス</span>
                          <select
                            value={editForm.status}
                            onChange={(event) =>
                              setEditForms((current) => ({
                                ...current,
                                [enrollment.enrollmentId]: {
                                  ...editForm,
                                  status: event.target.value as EnrollmentStatus
                                }
                              }))
                            }
                          >
                            <option value="pendingStart">{describeStatus("pendingStart")}</option>
                            <option value="active">{describeStatus("active")}</option>
                            <option value="paused">{describeStatus("paused")}</option>
                            <option value="ended">{describeStatus("ended")}</option>
                            <option value="cancelled">{describeStatus("cancelled")}</option>
                          </select>
                        </label>

                        <label style={{ display: "grid", gap: 4 }}>
                          <span>開始日</span>
                          <input
                            type="date"
                            value={editForm.joinDate}
                            onChange={(event) =>
                              setEditForms((current) => ({
                                ...current,
                                [enrollment.enrollmentId]: {
                                  ...editForm,
                                  joinDate: event.target.value
                                }
                              }))
                            }
                          />
                        </label>

                        <label style={{ display: "grid", gap: 4 }}>
                          <span>終了日</span>
                          <input
                            type="date"
                            value={editForm.leaveDate}
                            onChange={(event) =>
                              setEditForms((current) => ({
                                ...current,
                                [enrollment.enrollmentId]: {
                                  ...editForm,
                                  leaveDate: event.target.value
                                }
                              }))
                            }
                          />
                        </label>

                        <label style={{ display: "grid", gap: 4 }}>
                          <span>内部メモ</span>
                          <textarea
                            value={editForm.notesInternal}
                            onChange={(event) =>
                              setEditForms((current) => ({
                                ...current,
                                [enrollment.enrollmentId]: {
                                  ...editForm,
                                  notesInternal: event.target.value
                                }
                              }))
                            }
                            rows={3}
                          />
                        </label>
                      </div>

                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => void handlePatchEnrollment(enrollment.enrollmentId)}
                          disabled={busyKey === `patch:${enrollment.enrollmentId}`}
                        >
                          {busyKey === `patch:${enrollment.enrollmentId}` ? "保存中..." : "更新する"}
                        </button>

                        {getTransitionActions(enrollment.status).map((toStatus) => (
                          <button
                            key={toStatus}
                            type="button"
                            onClick={() => void handleTransition(enrollment, toStatus)}
                            disabled={busyKey === `transition:${enrollment.enrollmentId}:${toStatus}`}
                          >
                            {describeStatus(toStatus)}へ
                          </button>
                        ))}

                        {enrollment.enrollmentType === "trial" ? (
                          <button
                            type="button"
                            onClick={() => void handleConvertToRegular(enrollment)}
                            disabled={busyKey === `convert:${enrollment.enrollmentId}`}
                          >
                            {busyKey === `convert:${enrollment.enrollmentId}` ? "変換中..." : "通常所属へ変換"}
                          </button>
                        ) : null}
                      </div>

                      {enrollment.trialPolicySnapshot?.trialPolicyId ? (
                        <div style={{ fontSize: 12, opacity: 0.7 }}>
                          体験ポリシー: {enrollment.trialPolicySnapshot.trialPolicyId}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
